import React, { useEffect, useState, useRef, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFirestore } from "../../firebase/FirestoreContext";
import { getDoc, doc, updateDoc, deleteDoc, Timestamp, addDoc, collection, writeBatch, getDocs } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EnvContext } from "../../../context/EnvContext";
import config from "../../../config";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const EditKnowledge = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const environment = useContext(EnvContext);
  const db = useFirestore();
  const storage = getStorage();
  const fileInputRef = useRef(null);

  const collectionPath = environment === "staging" ? config.enveironment.staging.collectionPath : config.enveironment.production.collectionPath;

  const [knowledgeData, setKnowledgeData] = useState({
    articleTitle: "",
    title: "",
    description: "",
    shortDescription: "",
    image: "",
    modalVisible: false,
    customURL: "",
    createdAt: Timestamp.fromDate(new Date()),
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchKnowledgeData = async () => {
      if (id) {
        try {
          const docRef = doc(db, `${collectionPath}Help/Knowledge/DataBase`, id);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            // Overwrite the entire state with fetched data
            setKnowledgeData({
              ...docSnap.data(),
            });
          }
        } catch (error) {
          console.error("Error fetching knowledge data:", error);
        }
      } else {
        setKnowledgeData({
          articleTitle: "",
          title: "",
          description: "",
          shortDescription: "",
          image: "",
          modalVisible: false,
          customURL: "",
          createdAt: Timestamp.fromDate(new Date()),
        });
      }
    };

    fetchKnowledgeData();
  }, [id, db, collectionPath]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const updatedKnowledge = {
        ...knowledgeData,
        createdAt: knowledgeData.createdAt || Timestamp.fromDate(new Date()),
        sortOrder: 0,
        featured: false,
      };

      if (id) {
        const knowledgeRef = doc(db, `${collectionPath}Help/Knowledge/DataBase`, id);
        await updateDoc(knowledgeRef, updatedKnowledge);
        toast.success("Knowledge updated successfully!");
      } else {
        // For a new article, get all existing articles and increment sortOrder by 1.
        const knowledgeCollectionRef = collection(db, `${collectionPath}Help/Knowledge/DataBase`);
        const snapshot = await getDocs(knowledgeCollectionRef);
        const batch = writeBatch(db);
        snapshot.forEach((docSnap) => {
          const docData = docSnap.data();
          const newSortOrder = docData.sortOrder !== undefined ? docData.sortOrder + 1 : 1;
          batch.update(doc(db, `${collectionPath}Help/Knowledge/DataBase`, docSnap.id), { sortOrder: newSortOrder });
        });
        await batch.commit();

        await addDoc(knowledgeCollectionRef, updatedKnowledge);
        toast.success("Knowledge created successfully!");
      }
      navigate("/main/knowledge");
    } catch (error) {
      toast.error("Error saving knowledge!");
      console.error("Error saving knowledge:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm("Are you sure you want to delete this knowledge entry?");
    if (!confirmed) return;
    try {
      const knowledgeRef = doc(db, `${collectionPath}Help/Knowledge/DataBase`, id);
      await deleteDoc(knowledgeRef);
      toast.success("Knowledge deleted successfully!");
      navigate("/main/knowledge");
    } catch (error) {
      toast.error("Error deleting knowledge!");
      console.error("Error deleting knowledge:", error);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setLoading(true);
    try {
      const storageRef = ref(storage, `Knowledge/${id || new Date().getTime().toString()}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setKnowledgeData((prev) => ({ ...prev, image: url }));
      toast.success("Image uploaded successfully!");
    } catch (error) {
      toast.error("Error uploading image!");
      console.error("Error uploading image:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDescriptionChange = (value) => {
    setKnowledgeData((prevData) => ({ ...prevData, description: value }));
  };

  return (
    <div>
      <style>
        {`
            .ql-editor {
            background: white;
            }
            .ql-toolbar {
            }`}
      </style>
      <ToastContainer position="top-center" autoClose={5000} hideProgressBar />
      <h2 style={styles.title}>{id ? "Edit Knowledge" : "New Knowledge"}</h2>
      <span style={{ fontSize: 12, color: "#4a4141", paddingBottom: 10 }}>{id}</span>
      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.formElement}>
          <label style={styles.formLabel}>Article Title:</label>
          <input
            type="text"
            value={knowledgeData.articleTitle}
            onChange={(e) => setKnowledgeData({ ...knowledgeData, articleTitle: e.target.value })}
            style={styles.formInput}
          />
        </div>
        <div style={styles.formElement}>
          <label style={styles.formLabel}>Title:</label>
          <input
            type="text"
            value={knowledgeData.title}
            onChange={(e) => setKnowledgeData({ ...knowledgeData, title: e.target.value })}
            style={styles.formInput}
          />
        </div>
        <div style={styles.formElement}>
          <label style={styles.formLabel}>Image:</label>
          {knowledgeData.image && <img src={knowledgeData.image} alt="Header" style={styles.imagePreview} />}
          <input type="file" ref={fileInputRef} onChange={handleFileUpload} />
        </div>
        <div style={styles.formElement}>
          <label style={styles.formLabel}>Short Description:</label>
          <textarea
            value={knowledgeData.shortDescription}
            onChange={(e) => setKnowledgeData({ ...knowledgeData, shortDescription: e.target.value })}
            style={styles.formTextarea}
          />
        </div>
        <div style={{ ...styles.formElement, display: "flex", height: 200, marginBottom: 20 }}>
          <label style={styles.formLabel}>Description:</label>
          {/* Use a unique key that depends on the article id so ReactQuill remounts when switching */}
          <ReactQuill
            key={id || "new"}
            value={knowledgeData.description}
            onChange={handleDescriptionChange}
            style={{ background: "white", border: "0px", height: 160 }}
          />
        </div>
        <div style={{ ...styles.formElement, display: "flex", top: 30 }}>
          <label style={styles.formLabel}>Display on modal:</label>
          <input
            type="checkbox"
            checked={knowledgeData.modalVisible}
            onChange={(e) => setKnowledgeData({ ...knowledgeData, modalVisible: e.target.checked })}
          />
        </div>
        <div style={styles.formElement}>
          <label style={styles.formLabel}>Custom URL:</label>
          <input
            type="text"
            value={knowledgeData.customURL}
            onChange={(e) => setKnowledgeData({ ...knowledgeData, customURL: e.target.value })}
            style={styles.formInput}
          />
        </div>
        <div style={styles.buttonGroup}>
          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </button>
          {id && (
            <button type="button" style={styles.deleteButton} onClick={handleDelete}>
              Delete
            </button>
          )}
          <button type="button" style={styles.cancelButton} onClick={() => navigate("/main/knowledge")}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

const styles = {
  title: {
    fontWeight: "200",
    paddingBottom: "10px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "20px",
    border: "1px solid #ccc",
    borderRadius: "5px",
    backgroundColor: "#f9f9f9",
  },
  formElement: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  },
  formLabel: {
    fontWeight: "bold",
    fontSize: "14px",
    color: "#4a4141",
  },
  formInput: {
    padding: "10px",
    fontSize: "16px",
    border: "1px solid #ccc",
    borderRadius: "5px",
  },
  formTextarea: {
    padding: "10px",
    fontSize: "16px",
    border: "1px solid #ccc",
    borderRadius: "5px",
    height: "100px",
    resize: "vertical",
  },
  imagePreview: {
    maxWidth: "200px",
    maxHeight: "150px",
    marginTop: "10px",
    borderRadius: "5px",
  },
  buttonGroup: {
    display: "flex",
    justifyContent: "flex-start",
    gap: "10px",
    marginTop: "10px",
  },
  button: {
    backgroundColor: "#478169",
    color: "white",
    padding: "10px 20px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    fontSize: "14px",
  },
  deleteButton: {
    backgroundColor: "red",
    color: "white",
    padding: "10px 20px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    fontSize: "14px",
  },
  cancelButton: {
    backgroundColor: "#ccc",
    color: "black",
    padding: "10px 20px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    fontSize: "14px",
  },
};

export default EditKnowledge;
